import { Log } from "@accurx/shared";
import {
    PrincipalSystem,
    SupportedPatientSearchType,
} from "domains/native/schemas/MedicalRecordConnectionDtoSchema";
import { MedicalRecordConnection } from "domains/native/schemas/MedicalRecordConnectionSchema";

import { useDesktopVersion } from "./useDesktopVersion";
import { useMedicalRecordConnectionQuery } from "./useMedicalRecordConnectionQuery";

const openInEMRSupport: Record<PrincipalSystem, boolean> = {
    Emis: true,
    SystmOne: true,
    Vision: true,
    Server: false,
    Unknown: false,
};

const saveToRecordSupport: Record<PrincipalSystem, boolean> = {
    Emis: true,
    SystmOne: true,
    Vision: true,
    Server: true,
    Unknown: false,
};

export const useMedicalRecordConnection = (): MedicalRecordConnection => {
    const { data: medicalRecordConnection } = useMedicalRecordConnectionQuery();
    const desktopVersion = useDesktopVersion();

    if (!medicalRecordConnection) {
        // Not connected to the toolbar/native api
        return {
            status: null,
            system: null,
            userMedicalRecordRole: null,
            hasMedicalRecordUserChanged: null,
            loggedInUsersDetails: null,
            capabilities: null,
        };
    }

    const {
        principalSystemType: system,
        connectionType: status,
        userMedicalRecordRole,
        capabilities,
        hasMedicalRecordUserChanged,
        loggedInUsersDetails,
    } = medicalRecordConnection;

    /**
     * There is a chance that the client is connected to an old toolbar version that doesn't provide the `SupportedPatientSearchType` field.
     * In this case we will fallback to deciding the patient search type based on the EMR system.
     */
    const getFallbackSupportedPatientSearchType =
        (): SupportedPatientSearchType => {
            Log.debug(
                "No supported patient search type capability was provided from the Native Bridge",
                {
                    tags: {
                        system,
                        desktopVersion,
                    },
                },
            );

            if (system === "Emis") return "QueryString";
            if (system === "SystmOne") return "Structured";

            return "None";
        };

    const supportedPatientSearchType =
        capabilities.supportedPatientSearchType ??
        getFallbackSupportedPatientSearchType();

    return {
        status,
        system,
        userMedicalRecordRole,
        hasMedicalRecordUserChanged,
        loggedInUsersDetails,
        capabilities: {
            openInEMR: status === "Connected" && openInEMRSupport[system],
            saveToRecord: status === "Connected" && saveToRecordSupport[system],
            attachDocumentsFromRecord: capabilities.attachDocumentsFromRecord,
            saveAttachmentsToRecord: capabilities.saveAttachmentsToRecord,
            prefetchAttachments: capabilities.shouldPrefetchAttachments,
            supportedPatientSearchType,
        },
    };
};
